import { PairingShader } from "./shaders/PairingShader";
import { Camera } from "../../../SharedGL/Camera";
import { PairingBuffer } from "./shaders/PairingBuffer";
import { CrewSchedEvent, isIDisplayPairing } from "src/redux/reducers/CrewScheduleReducer";
import { addPairingToArr } from "./PairingsGL";
import { addNFAToArr } from "./NonFlyingAssignmentsGL";
import { getPlannedEventsInRange } from "../TimelineUtils";
import { IDisplayPairing, INonFlyingAssignment } from "@vmosolutions/oc-interfaces";

const pColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  pColors.push(...[0.0, 0.0, 0.0, 0.5]); //[0.54, 0.55, 0.52, 1.0]);
});

const nColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  nColors.push(...[0.0, 1.0, 0.0, 0.5]);
});

const pairingFlightColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  pairingFlightColors.push(...[0.0, 0.0, 1.0, 0.5]);
});

interface IPlannedEventsVertexOffset {
  pairingOffset: number;
  pairingVertexCount: number;
  nfaOffset: number;
  nfaVertexCount: number;
}

export class PlannedEventsGL {
  private pairingBuffer: PairingBuffer;
  private eventBuffer: PairingBuffer;
  public static pairingYStart = 55;
  public static pairingHeight = 25;
  private vertexCount = 0;
  private plannedEvents: CrewSchedEvent[][];
  private plannedEventOffsets: IPlannedEventsVertexOffset[] = [];
  private nonOverlappedPlannedEvents: CrewSchedEvent[] = [];
  public lastModified = 0;
  public plannedPairings: IDisplayPairing[] = [];
  public plannedNFAs: INonFlyingAssignment[] = [];

  private nfaVertexCount = 0;

  constructor(gl: WebGLRenderingContext, pairingShader: PairingShader) {
    this.pairingBuffer = new PairingBuffer(gl, pairingShader);
    this.eventBuffer = new PairingBuffer(gl, pairingShader);
  }

  public updatePlannedEventsBuffers(
    gl: WebGLRenderingContext,
    plannedEvents: CrewSchedEvent[][],
    lastModified: number
  ): void {
    this.lastModified = lastModified;
    // this.flightIndexesInRange = [0, flightData.length - 1];
    this.plannedEvents = plannedEvents;
    const pairingPositions: number[] = [];
    const pairingColors: number[] = [];
    // iterating over an aircraft (row) so height will always be same
    const y0 = PlannedEventsGL.pairingYStart;
    const y = y0 + PlannedEventsGL.pairingHeight;

    const nfaPositions: number[] = [];
    const nfaColors: number[] = [];

    this.plannedEventOffsets = [];
    this.nonOverlappedPlannedEvents = [];
    plannedEvents.forEach((overlappingCrewSchedEvents: CrewSchedEvent[]) => {
      const pairingOffset = pairingPositions.length / 2;
      const nfaOffset = nfaPositions.length / 2;
      this.nonOverlappedPlannedEvents.push(overlappingCrewSchedEvents[0]);

      const indHeight = PlannedEventsGL.pairingHeight / overlappingCrewSchedEvents.length;
      overlappingCrewSchedEvents.forEach((overlappingCrewSchedEvent: CrewSchedEvent, index: number) => {
        if (isIDisplayPairing(overlappingCrewSchedEvent)) {
          addPairingToArr(overlappingCrewSchedEvent, y0, y, pairingPositions, pairingColors, nfaPositions, nfaColors);
          this.plannedPairings.push(overlappingCrewSchedEvent);
        } else {
          addNFAToArr(
            overlappingCrewSchedEvent,
            y0 + index * indHeight,
            y0 + (index + 1) * indHeight - 3,
            pairingPositions,
            pairingColors /*, nfaPositions, nfaColors */
          );
          this.plannedNFAs.push(overlappingCrewSchedEvent);
        }
      });

      this.plannedEventOffsets.push({
        pairingOffset,
        pairingVertexCount: pairingPositions.length / 2 - pairingOffset,
        nfaOffset,
        nfaVertexCount: nfaPositions.length / 2 - nfaOffset,
      });
    });

    this.pairingBuffer.updateBuffers(gl, pairingPositions, pairingColors);
    this.eventBuffer.updateBuffers(gl, nfaPositions, nfaColors);
    this.vertexCount = pairingPositions.length / 2;
    this.nfaVertexCount = nfaPositions.length / 2;
  }

  public draw = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    if (this.vertexCount === 0 && this.nfaVertexCount === 0) {
      return;
    }

    const range = getPlannedEventsInRange(camera, this.nonOverlappedPlannedEvents);
    const minOffset = this.plannedEventOffsets[range.minIndex];
    const maxOffset = this.plannedEventOffsets[range.maxIndex];

    const pairingOffset = minOffset.pairingOffset;
    const pairingVC = maxOffset.pairingOffset + maxOffset.pairingVertexCount - pairingOffset;

    // if (this.vertexCount > 0) {
    if (pairingOffset >= 0 && pairingVC >= 0 && pairingVC <= this.vertexCount) {
      this.pairingBuffer.draw(gl, camera, yStart, -2, pairingOffset, pairingVC);
    }

    const nfaOffset = minOffset.nfaOffset;
    const nfaVC = maxOffset.nfaOffset + maxOffset.nfaVertexCount - nfaOffset;
    // if (this.nfaVertexCount > 0) {
    if (nfaOffset >= 0 && nfaVC >= 0 && nfaVC <= this.nfaVertexCount) {
      this.eventBuffer.draw(gl, camera, yStart, -1.9, nfaOffset, nfaVC);
    }
  };
}
